import axios, { AxiosResponse } from "axios";
import {
  Config,
  FranticFanficUser,
  GameSettings,
  PlayerDetails,
  PlayerId,
  Room,
  RoomMeta,
  TwitchStreamer,
  UserPrompt, // Added
  UserPromptPack, // Added
} from "./types";
import { config } from "./config";

class RoomAPI {
  static prefix = config.apiHost + "api/v3";

  static GetCount() {
    return axios.get(RoomAPI.prefix + "/counts/").then((res) => res.data.count);
  }

  static IncrCount() {
    return axios
      .post(RoomAPI.prefix + "/counts/")
      .then((res) => res.data.count);
  }

  static JoinRoom(
    roomCode: string,
    playerDetails: PlayerDetails,
    token?: string
  ): Promise<PlayerId> {
    const headers = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};
    return axios
      .put(
        RoomAPI.prefix + "/rooms/" + roomCode + "/players",
        {
          name: playerDetails.name,
          secret: playerDetails.secret,
          meta: {
            sprite_id: playerDetails.meta.sprite_id,
            mature: playerDetails.meta.mature,
            accessibility2: playerDetails.meta.accessibility2,
          },
          user_id: playerDetails.user_id,
        },
        headers
      )
      .then((res) => res.data);
  }

  static JoinRoomAudience(
    roomCode: string,
    playerDetails: PlayerDetails,
    token?: string
  ): Promise<PlayerId> {
    const headers = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};

    return axios
      .put(
        RoomAPI.prefix + "/rooms/" + roomCode + "/audience",
        {
          name: playerDetails.name,
          secret: playerDetails.secret,
          meta: {
            sprite_id: playerDetails.meta.sprite_id,
            mature: playerDetails.meta.mature,
            accessibility2: playerDetails.meta.accessibility2,
          },
          user_id: playerDetails.user_id,
        },
        headers
      )
      .then((res) => res.data);
  }

  static GetRoom(roomCode: string): Promise<Room> {
    return axios
      .get(RoomAPI.prefix + "/rooms/" + roomCode)
      .then((res) => res.data);
  }

  static ListRooms(): Promise<Room[]> {
    return axios.get(RoomAPI.prefix + "/rooms/").then((res) => res.data);
  }

  static CountRooms(): Promise<{ count: number }> {
    return axios.get(RoomAPI.prefix + "/rooms/count").then((res) => res.data);
  }

  static CreateRoom(
    settings: GameSettings,
    roomMeta: RoomMeta,
    me: PlayerDetails,
    auth0Id?: string,
    token?: string
  ): Promise<Room> {
    const headers = token
      ? { headers: { Authorization: `Bearer ${token}` } }
      : {};

    return axios
      .post(
        RoomAPI.prefix + "/rooms/",
        {
          settings: settings,
          host: {
            name: me.name,
            meta: {
              sprite_id: me.meta.sprite_id,
              mature: me.meta.mature,
              accessibility2: me.meta.accessibility2,
            },
            secret: me.secret,
            user_id: auth0Id,
          },
          meta: roomMeta,
        },
        headers
      )
      .then((res) => res.data);
  }


  static GetUser(token: string): Promise<FranticFanficUser> {
    return axios
      .get(RoomAPI.prefix + "/users", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => r.data);
  }

  static GetTwitchUsers(): Promise<TwitchStreamer[]> {
    return axios
      .get(RoomAPI.prefix + "/twitch/streams")
      .then((res) => res.data);
  }

  static GetConfig(): Promise<Config> {
    return axios.get(RoomAPI.prefix + "/config").then((res) => res.data);
  }

  static GetAdminAccess(secret: string): Promise<number> {
    return axios
      .get(RoomAPI.prefix + "/config/access", {
        headers: { supersecretadminkey: secret },
      })
      .then((r) => r.status);
  }

  static UpdateConfig(config: Config, secret: string): Promise<Config> {
    return axios
      .put(RoomAPI.prefix + "/config", config, {
        headers: { supersecretadminkey: secret },
      })
      .then((res) => res.data);
  }

  // --- Prompt Pack API Methods ---

  static GetPromptPacks(token: string): Promise<UserPromptPack[]> {
    return axios
      .get(RoomAPI.prefix + "/promptPacks", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => res.data);
  }

  static CreatePromptPack(
    name: string,
    token: string
  ): Promise<UserPromptPack> {
    return axios
      .post(
        RoomAPI.prefix + "/promptPacks/",
        { name },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => res.data);
  }

  static DeletePromptPack(
    packId: number,
    token: string
  ): Promise<AxiosResponse> {
    return axios.delete(RoomAPI.prefix + `/promptPacks/${packId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  static GetPrompts(packId: number, token: string): Promise<UserPrompt[]> {
    return axios
      .get(RoomAPI.prefix + `/promptPacks/${packId}/prompts`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => res.data);
  }

  static CreatePrompt(
    packId: number,
    prompt: string,
    token: string
  ): Promise<UserPrompt> {
    return axios
      .post(
        RoomAPI.prefix + `/promptPacks/${packId}/prompts`,
        { prompt },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => res.data);
  }

  static DeletePrompt(promptId: number, token: string): Promise<AxiosResponse> {
    return axios.delete(RoomAPI.prefix + `/promptPacks/prompts/${promptId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  static CreateCheckout(
    token: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ url: string }> {
    return axios
      .post(
        RoomAPI.prefix + "/stripe/checkout",
        { success_url: successUrl, cancel_url: cancelUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => res.data);
  }

  static SyncStripeStatus(token: string): Promise<{ is_supporter: boolean }> {
    return axios
      .post(
        RoomAPI.prefix + "/stripe/sync",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => res.data);
  }
}

export default RoomAPI;
